import React from "react";
import { Link } from "react-router-dom";
import EmployeePageShell from "./EmployeePageShell";
import useFetch from "../../useFetch";
import { getStoredUser } from "../../auth";
import AccountReferralCard from "../Common/AccountReferralCard";

const formatDeadline = (job) => {
  if (!job.application_deadline) return "Open until filled";

  return "Deadline: " + new Date(job.application_deadline).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDate = (date) => date ? new Date(date).toLocaleDateString() : "-";

const getDaysRemaining = (date) => {
  if (!date) return null;
  const today = new Date();
  const endDate = new Date(date);
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)));
};

const getPlanEndLabel = (subscription) => {
  if (!subscription?.ends_at) return "Plan date";
  if (subscription?.plan?.billing_type !== "subscription") return "Valid until";
  if (subscription?.status === "canceled") return "Ended on";
  return subscription?.cancel_at_period_end ? "Expires on" : "Renews on";
};

const EmployeeDashboard = () => {
  const storedUser = getStoredUser();
  const { response, isLoading, error } = useFetch(process.env.REACT_APP_BASE_API_URL + "/employer/dashboard");
  const dashboard = response?.dashboard;
  const profileCompletion = dashboard?.company_profile_completion ?? {};
  const missingProfileSections = profileCompletion?.missing_sections ?? [];
  const account = dashboard?.account ?? storedUser;
  const planEndDate = dashboard?.current_plan?.current_period_ends_at ?? dashboard?.current_plan?.ends_at;
  const daysRemaining = getDaysRemaining(planEndDate);
  const availableJobPosts = Number(dashboard?.available_job_posts ?? 0);
  const hasCredits = availableJobPosts > 0;

  const stats = [
    { id: "active-jobs", value: dashboard?.stats?.active_jobs ?? 0, label: "Active Jobs", icon: "flaticon-briefcase" },
    { id: "applicants", value: dashboard?.stats?.applicants ?? 0, label: "Applicants", icon: "la la-users", className: "ui-green" },
    { id: "expiring-soon", value: dashboard?.stats?.expiring_soon ?? 0, label: "Expiring Soon", icon: "la la-clock", className: "ui-yellow" },
    { id: "expired-jobs", value: dashboard?.stats?.expired_jobs ?? 0, label: "Expired Jobs", icon: "la la-calendar-times", className: "ui-red" },
  ];

  return (
    <EmployeePageShell
      active="dashboard"
      title="Employer Dashboard"
      text="A quick overview of your job postings and applicants."
    >
      {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
      {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

      {!isLoading && (
        <div className={`employee-credit-hero ${hasCredits ? "" : "is-empty"}`}>
          <div className="employee-credit-hero-count">
            <strong>{availableJobPosts}</strong>
          </div>
          <div className="employee-credit-hero-copy">
            <h4>{hasCredits ? "You have credits ready to use." : "Add job post credits before posting."}</h4>
            <p>{hasCredits ? "Each approved employer job post uses one credit. Buy more anytime from Plans & Payments." : "Purchase a Single Job Post or Best Value 12-pack so your next approved job can go live."}</p>
          </div>
          <div className="employee-credit-hero-actions">
            <Link to="/employee-plans" className="theme-btn btn-style-one">Buy Credits</Link>
            <Link to="/employee-post-job" className="theme-btn btn-style-three">Post a Job</Link>
          </div>
        </div>
      )}

      <div className="row">
        {stats.map((item) => (
          <div className="col-lg-3 col-md-6 col-sm-12" key={item.id}>
            <div className={`ui-item ${item.className || ""}`}>
              <div className="left"><i className={`icon ${item.icon}`}></i></div>
              <div className="right">
                <h4>{item.value}</h4>
                <p>{item.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="ls-widget">
            <div className="widget-title">
              <h4>Recent Job Posts</h4>
              <div className="employee-widget-actions">
                <Link to="/employee-plans" className="theme-btn btn-style-three">Buy Credits</Link>
                <Link to="/employee-post-job" className="theme-btn btn-style-one">Post Job</Link>
              </div>
            </div>
            <div className="widget-content">
              <div className="employee-simple-list">
                {(dashboard?.recent_jobs ?? []).map((job) => (
                  <div className="employee-list-item" key={job.id}>
                    <div>
                      <strong>{job.title}</strong>
                      <span>{job.applications_count} applicants - {formatDeadline(job)}</span>
                    </div>
                    <span>{job.status}</span>
                  </div>
                ))}
              </div>
              {!isLoading && (dashboard?.recent_jobs ?? []).length === 0 && (
                <div className="employee-empty-state">No jobs posted yet.</div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="ls-widget employee-account-widget">
            <div className="widget-title">
              <h4>Account Referral</h4>
            </div>
            <div className="widget-content">
              <AccountReferralCard account={account} />
            </div>
          </div>

          <div className="ls-widget employee-completion-widget">
            <div className="widget-title">
              <h4>Company Profile</h4>
              <Link to="/employee-company-profile" className="theme-btn btn-style-three">Edit</Link>
            </div>
            <div className="widget-content">
              <div className="dashboard-completion-card">
                <div className="dashboard-completion-head">
                  <strong>{profileCompletion?.percentage ?? 0}%</strong>
                  <span>{profileCompletion?.completed ?? 0} of {profileCompletion?.total ?? 0} sections complete</span>
                </div>
                <div className="dashboard-completion-bar">
                  <span style={{ width: `${profileCompletion?.percentage ?? 0}%` }}></span>
                </div>
                {missingProfileSections.length > 0 ? (
                  <>
                    <p>Missing business details</p>
                    <ul>
                      {missingProfileSections.slice(0, 5).map((section) => (
                        <li key={section.key}>{section.label}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p>Your company profile has the key details candidates expect.</p>
                )}
              </div>
            </div>
          </div>

          <div className="ls-widget">
            <div className="widget-title">
              <h4>Posting Credits</h4>
            </div>
            <div className="widget-content">
              <div className={`employee-plan-card employee-credit-card ${hasCredits ? "" : "is-empty"}`}>
                <span>{dashboard?.current_plan?.plan?.name ?? "Posting access"}</span>
                <strong>{availableJobPosts} job post{availableJobPosts === 1 ? "" : "s"} available</strong>
                <p>{planEndDate ? `${getPlanEndLabel(dashboard?.current_plan)} ${formatDate(planEndDate)}${daysRemaining !== null ? `, ${daysRemaining} day${daysRemaining === 1 ? "" : "s"} remaining` : ""}.` : "Unused purchased credits stay available until they are used."}</p>
                <Link to="/employee-plans" className="theme-btn btn-style-one">{hasCredits ? "Buy More Credits" : "Purchase Credits"}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </EmployeePageShell>
  );
};

export default EmployeeDashboard;
